import React from 'react';

import {assert} from 'Asserts';
import {MinimalFolder} from 'constants/FolderConstants';
import {PROJECT_TYPE} from 'constants/SourceConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import FolderDAO from 'file-browser/FolderDAO';
import FolderSelectTree from 'file-browser/FolderSelectTree';
import {FilterKey, GenericFilter} from 'search/SearchConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import MenuPopover from 'ui-library/menus/MenuPopover';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import PrimaryFilterFormSkeleton from './base/PrimaryFilterFormSkeleton';

interface FolderFilterFormProps extends BaseFilterFormProps {
  filter: GenericFilter<FilterKey.FOLDER>;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: GenericFilter<FilterKey.FOLDER>) => void;
  onRemove?: () => void;
}

export interface FolderFilterFormExtraProps {
  onlyProjects?: boolean;
}

interface State {
  folders: Array<MinimalFolder> | null;
}

class FolderFilterForm extends React.Component<FolderFilterFormProps & FolderFilterFormExtraProps, State> {
  state: State = {
    folders: null,
  };

  componentDidMount(): void {
    FolderDAO.fetchAll({level: 'read'}).done((folders: Array<MinimalFolder>) => {
      this.setState({folders});
    });
  }

  _handleSelectFolder = (folderId: string | null): void => {
    this.props.onChangeFilter({
      ...this.props.filter,
      value: folderId,
    });
  };

  _getSelectableFolders = (): Array<MinimalFolder> | null => {
    let selectableFolders = this.state.folders;
    if (selectableFolders && this.props.onlyProjects) {
      const isProjectSourcePredicate = (folder: MinimalFolder): boolean =>
        Boolean(folder.source && folder.source.sourceType === PROJECT_TYPE);
      const isProjectRootFolderPredicate = FileBrowserUtil.propsToFolderPredicate({
        showInventory: false,
        showNotebook: false,
      });
      selectableFolders = selectableFolders.filter(
        (folder: MinimalFolder) => isProjectSourcePredicate(folder) && isProjectRootFolderPredicate(folder)
      );
    }
    return selectableFolders;
  };

  render(): JSX.Element {
    const skeletonProps = pickDefined(this.props, ...BASE_FILTER_FORM_PROP_NAMES);
    const selectableFolders = this._getSelectableFolders();
    const folderSelectTree =
      selectableFolders != null ? (
        <FolderSelectTree
          allowNone={true}
          className="u-no-spacing-bottom folderFilter-selectTree"
          folders={selectableFolders}
          folderIds={this.props.filter.value ? [String(this.props.filter.value)] : []}
          onChangeSelectedFolders={(folderIds: Array<string>) => {
            assert(folderIds.length <= 1, 'Expected 1 or fewer folders');
            this._handleSelectFolder(folderIds.length ? folderIds[0] : null);
          }}
          multiple={false}
        />
      ) : (
        <CenteredHorizontalThrobber />
      );
    if (this.props.filterStyle !== FilterStyle.PRIMARY) {
      throw new Error('FolderFilterForm not yet implemented as secondary filter');
    }
    return (
      <PrimaryFilterFormSkeleton
        {...skeletonProps}
        buttonSize={this.props.buttonSize}
        menuOverride={<MenuPopover>{folderSelectTree}</MenuPopover>}
        dropdownToggleText={this.props.onlyProjects ? 'Project' : this.props.dropdownToggleText}
      />
    );
  }
}

export default FolderFilterForm;
